<template>
  <!--
    Full-width grid of production cards for the home page.
    Groups items by packageKey — multi-hotel packages collapse to a single card
    (the cheapest hotel is representative) with a "מלונות נוספים (N-1)" button
    that opens ProductionPackageDialog listing the peers.

    Renders nothing when there are no productions AND no loading state.
    Feature flag: pass :enabled="false" to hide entirely (rollback lever).
  -->
  <div v-if="enabled && (loading || (items && items.length))" class="productions-grid-wrap">
    <div class="productions-grid">
      <template v-if="loading">
        <!-- Skeleton cards while the API call is in flight — keeps layout stable. -->
        <div v-for="i in 4" :key="`sk-${i}`" class="production-skeleton">
          <div class="production-skeleton__image"></div>
          <div class="production-skeleton__body">
            <div class="production-skeleton__line production-skeleton__line--title"></div>
            <div class="production-skeleton__line production-skeleton__line--meta"></div>
            <div class="production-skeleton__line"></div>
            <div class="production-skeleton__line production-skeleton__line--short"></div>
            <div class="production-skeleton__line production-skeleton__line--price"></div>
          </div>
        </div>
      </template>
      <template v-else>
        <ProductionCard
          v-for="group in groups"
          :key="group.key"
          :production="group.representative"
          :packagePeerCount="group.items.length"
          @book="onBook"
          @open-package="openPackage(group)"
        />
      </template>
    </div>

    <ProductionPackageDialog
      :visible="dialogVisible"
      :packageKey="dialogPackageKey"
      :items="dialogItems"
      @close="dialogVisible = false"
      @book="onBook"
    />
  </div>
</template>

<script>
import ProductionCard from './ProductionCard';
import ProductionPackageDialog from './ProductionPackageDialog';

export default {
  name: 'ProductionsGrid',
  components: { ProductionCard, ProductionPackageDialog },
  props: {
    // Feature flag — set false to disable the whole feature without touching integrations.
    enabled: { type: Boolean, default: true },
    // Flat array of production items (API shape). Empty [] with loading=true → skeletons.
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      dialogVisible: false,
      dialogPackageKey: '',
      dialogItems: [],
    };
  },
  computed: {
    // Group by packageKey. Solo items (packageKey === null) are their own group,
    // keyed off their ref so v-for keys stay unique.
    groups() {
      const map = new Map();
      (this.items || []).forEach((it) => {
        const key = it.packageKey ? `pkg:${it.packageKey}` : `solo:${it.ref}`;
        if (!map.has(key)) map.set(key, { key, packageKey: it.packageKey || null, items: [] });
        map.get(key).items.push(it);
      });
      // Representative = cheapest in the group (best hook price).
      return [...map.values()].map((g) => ({
        ...g,
        representative: [...g.items].sort(
          (a, b) => (Number(a.totalPrice) || 0) - (Number(b.totalPrice) || 0),
        )[0],
      }));
    },
  },
  methods: {
    openPackage(group) {
      this.dialogPackageKey = group.packageKey || group.representative.hotelName;
      this.dialogItems = group.items;
      this.dialogVisible = true;
    },
    onBook(item) {
      // Navigate straight to the standard product page. `productPath` is the
      // relative URL Flying built themselves (it includes dealId +
      // dealCategoryId, which the product page needs to open the right deal),
      // so we prefer it verbatim. Fall back to a manual build only if the
      // feed ever ships without it.
      const url = item && item.productPath ? item.productPath : this.buildProductPageUrlFallback(item);
      if (!url) return;
      // Emit for parents that want to intercept (analytics, guards, etc.).
      this.$emit('book', item);
      if (url.startsWith('/')) {
        // In-app SPA navigation via router — keeps state.
        this.$router.push(url).catch(() => { /* NavigationDuplicated is fine */ });
      } else {
        window.location.href = url;
      }
    },
    // Fallback URL builder — mirrors an existing production-page link so the
    // fallback opens the same page shape the site already handles. Reference
    // (Ofir 2026-08-15):
    //   /product-page?city=TLV&hotelCode=4086&checkIn=…&checkOut=…&adult=2
    //     &child=0&infant=0&rooms=0&lang=he&includeFlight&returnLog
    //     &manualDiscount=0&category=D&roomCode=ST&basisCode=BB&priceList
    //     &suppliers=ATLANTIS
    // Used only when the productions feed doesn't ship productPath (which is
    // the preferred field and already includes dealId + dealCategoryId).
    buildProductPageUrlFallback(item) {
      if (!item) return '';
      const parts = [];
      const push = (k, v) => {
        // Match the reference: params like `includeFlight`, `returnLog`,
        // `priceList` appear with no `=` when empty. Everything else is
        // key=value (URL-encoded).
        if (v === undefined || v === null || v === '') {
          parts.push(k);
        } else {
          parts.push(`${k}=${encodeURIComponent(String(v))}`);
        }
      };
      const lang = (this.$store && this.$store.state && this.$store.state.language) || 'he';
      push('city', item.cityCode);
      push('hotelCode', item.hotelCode);
      push('checkIn', item.checkin);
      push('checkOut', item.checkout);
      push('adult', item.adult);
      push('child', item.child);
      push('infant', item.infant);
      push('rooms', 0);
      push('lang', lang);
      push('includeFlight', '');
      push('returnLog', '');
      push('manualDiscount', 0);
      push('category', item.priceCategCode);
      push('roomCode', item.roomCode);
      push('basisCode', item.basisCode);
      push('priceList', item.priceList || '');
      push('suppliers', item.source);
      if (item.dealId) push('dealId', item.dealId);
      const catId = (item.categoryIds && item.categoryIds[0]) || '';
      if (catId) push('dealCategoryId', catId);
      return `/product-page?${parts.join('&')}`;
    },
  },
};
</script>

<style scoped>
.productions-grid-wrap {
  padding: 16px 24px 24px;
}
.productions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
}

/* Skeleton card — matches ProductionCard proportions so nothing shifts on load. */
.production-skeleton {
  background: #ffffff;
  border: 1px solid #e6ebf1;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(31, 76, 150, 0.06);
}
.production-skeleton__image {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: linear-gradient(90deg, #eef2f7 0%, #f6f8fb 50%, #eef2f7 100%);
  background-size: 200% 100%;
  animation: prodsk-shimmer 1.2s infinite linear;
}
.production-skeleton__body {
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.production-skeleton__line {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef2f7 0%, #f6f8fb 50%, #eef2f7 100%);
  background-size: 200% 100%;
  animation: prodsk-shimmer 1.2s infinite linear;
}
.production-skeleton__line--title { height: 16px; width: 70%; }
.production-skeleton__line--meta { width: 45%; }
.production-skeleton__line--short { width: 55%; }
.production-skeleton__line--price { width: 30%; height: 20px; margin-top: 6px; }

@keyframes prodsk-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
